import { useDraggable, useDroppable } from '@dnd-kit/core';
import { Box } from '@mui/material';
import { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { FOCUS_RING, tileRingSx } from '../chrome';
import { isPanelCell } from '../layout/layout';
import { ViewportCell } from '../layout/types';
import { RowHost } from './RowHost';

const FOCUS_RING_SX = {
    '&:focus': { outline: 'none' },
    '&:focus-visible::after': {
        content: '""',
        ...tileRingSx('2px', FOCUS_RING),
    },
};

interface DraggableCellProps {
    cell: ViewportCell;
    /** What the pane shows, for its accessible name. Never PHI. */
    name: string;
    /** The pane's 0-based address, for its accessible name and its drop rule. */
    rowIndex: number;
    columnIndex: number;
    host: RowHost;
    /** The tile, given the pointer listeners its title bar takes. */
    children: (dragHandleProps: Record<string, unknown>) => ReactNode;
}

/**
 * One pane of a bound row as the grid handles it: draggable with the
 * pointer by its tile's title bar to reorder within the row, a drop target
 * for its row's other panes, the roving Tab stop when it is the active
 * pane, and activated by the pointer entering it or focus reaching it. The
 * title bar takes the pointer listeners only: no key moves a pane, so it
 * is no button, no Tab stop and gives no drag instructions. What the pane
 * shows is the tile the children render.
 */
export default function DraggableCell({
    cell,
    name,
    rowIndex,
    columnIndex,
    host,
    children,
}: DraggableCellProps) {
    const intl = useIntl();
    const {
        listeners,
        setNodeRef: setDragRef,
        isDragging,
    } = useDraggable({ id: cell.id });
    const { setNodeRef: setDropRef, isOver } = useDroppable({ id: cell.id });
    const setRef = (node: HTMLElement | null) => {
        setDragRef(node);
        setDropRef(node);
    };
    // A pane may only drop within its own row, and a panel within the band:
    // a row never mixes datasets, and the band never mixes with the images.
    const showDropZone =
        isOver &&
        host.dragRowIndex === rowIndex &&
        host.dragIsPanel === isPanelCell(cell) &&
        host.activeDragId !== cell.id;

    return (
        <Box
            ref={setRef}
            role="group"
            aria-label={intl.formatMessage(
                { id: 'tile.cellName' },
                { name, row: rowIndex + 1, column: columnIndex + 1 }
            )}
            data-tile-id={cell.id}
            tabIndex={host.rovingTabTarget === cell.id ? 0 : -1}
            onMouseEnter={() => host.onActivate(cell.id)}
            onFocus={() => host.onActivate(cell.id)}
            sx={{
                position: 'relative',
                height: '100%',
                opacity: isDragging ? 0.4 : 1,
                ...FOCUS_RING_SX,
            }}
        >
            {children({ ...listeners })}
            {showDropZone ? (
                <Box
                    data-testid="drop-zone"
                    sx={{
                        ...tileRingSx('2px', 'primary.main'),
                        bgcolor: 'rgba(0, 165, 185, 0.14)',
                    }}
                />
            ) : null}
        </Box>
    );
}
